import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  CalendarClock, Loader2, CheckCircle2, XCircle, RotateCcw, MailWarning,
} from "lucide-react";

const fmtDt = (iso) => {
  if (!iso) return "TBD";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "long", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const defaultMessage = ({ customerName, businessName, jobTitle, originalSlot, newSlot, reason }) => {
  const rl = reason ? `\n\nReason: ${reason}` : "";
  return (
    `Hi ${customerName || "there"},\n\n` +
    `${businessName || "Your service team"} is reaching out about your appointment` +
    `${jobTitle ? ` for ${jobTitle}` : ""}.\n\n` +
    `Original: ${fmtDt(originalSlot)}\n` +
    `New time: ${fmtDt(newSlot)}` +
    `${rl}\n\n` +
    `Please reply if the new time doesn't work and we'll find another slot.\n\n` +
    `Thank you,\n${businessName || "Your service team"}`
  );
};

// UUID-ish key for idempotency (browser-safe fallback)
const makeIdempotencyKey = () =>
  (crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

export default function RescheduleConfirmDialog({
  open, onOpenChange, proposal, candidate, businessName, onSuccess,
}) {
  const [channel, setChannel] = useState("both"); // email | in_app | both
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);   // {ok, record_id, notification}
  const [error, setError] = useState(null);
  const [idempotencyKey, setIdempotencyKey] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const customerName = proposal?.customer_name || "";
  const jobTitle = proposal?.job_title || "";
  const originalSlot = proposal?.current_slot;
  const newSlot = candidate?.slot;
  const reason = candidate?.reason;
  const customerHasEmail = !!proposal?.customer_has_email;

  // Reset state when dialog re-opens for a new candidate
  useEffect(() => {
    if (!open) return;
    setResult(null);
    setError(null);
    setSubmitting(false);
    setRetrying(false);
    setIdempotencyKey(makeIdempotencyKey());
    setChannel(customerHasEmail ? "both" : "in_app");
    setMessage(defaultMessage({ customerName, businessName, jobTitle, originalSlot, newSlot, reason }));
  }, [open, proposal?.job_id, candidate?.slot]); // eslint-disable-line

  const channelsFor = useMemo(() => {
    if (channel === "email") return ["email"];
    if (channel === "in_app") return ["in_app"];
    return ["email", "in_app"];
  }, [channel]);

  const doConfirm = async () => {
    if (submitting || result) return; // double-click guard
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post(
        `/business-pulse/reschedule/${proposal.job_id}`,
        {
          new_scheduled_at: newSlot,
          reason,
          channels: channelsFor,
          message,
          original_scheduled_at: originalSlot,
          idempotency_key: idempotencyKey,
        },
      );
      setResult(data);
      if (data.notification?.ok) {
        toast.success("Reschedule confirmed and customer notified.");
      } else if (data.notification && Object.keys(data.notification?.channels || {}).length > 0) {
        toast.warning("Job rescheduled, but notification delivery failed. Retry available.");
      } else {
        toast.success("Reschedule confirmed.");
      }
      onSuccess?.(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Reschedule failed. Please try again.");
      toast.error(e.response?.data?.detail || "Reschedule failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const doRetryNotification = async () => {
    if (!result?.record_id || retrying) return;
    setRetrying(true);
    try {
      const { data } = await api.post(
        `/business-pulse/reschedule-notifications/${result.record_id}/retry`,
      );
      setResult((prev) => ({ ...prev, notification: data.notification }));
      if (data.notification?.ok) toast.success("Notification delivered.");
      else toast.error("Notification retry failed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed.");
    } finally {
      setRetrying(false);
    }
  };

  const closeAndReset = () => {
    onOpenChange(false);
  };

  const notification = result?.notification;
  const jobUpdated = !!result?.ok;
  const notifChannels = notification?.channels || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pulse-reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#FF5A00]" /> Confirm reschedule
          </DialogTitle>
          <DialogDescription>
            Nothing changes until you click <strong>Confirm reschedule</strong>. The job update and
            customer message are sent together.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <>
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-sm space-y-1">
              <div>
                <span className="text-zinc-500">Customer:</span>{" "}
                <span className="font-medium" data-testid="pulse-reschedule-customer-name">
                  {customerName || <span className="italic text-zinc-400">unknown</span>}
                </span>
              </div>
              <div>
                <span className="text-zinc-500">Job:</span>{" "}
                <span className="font-medium" data-testid="pulse-reschedule-service-name">{jobTitle || "—"}</span>
              </div>
              <div className="flex justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase text-zinc-500 tracking-widest">Original</div>
                  <div className="text-zinc-800" data-testid="pulse-reschedule-current-slot">{fmtDt(originalSlot)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase text-emerald-600 tracking-widest">Proposed</div>
                  <div className="text-emerald-700 font-medium" data-testid="pulse-reschedule-new-slot">{fmtDt(newSlot)}</div>
                </div>
              </div>
              {reason && (
                <div className="text-xs text-zinc-500 pt-1" data-testid="pulse-reschedule-reason">
                  <span className="font-mono-accent uppercase text-[10px] mr-1">reason</span>
                  {reason}
                </div>
              )}
            </div>

            <div className="mt-4">
              <Label className="text-[10px] uppercase font-mono-accent text-zinc-500">
                Notify customer via
              </Label>
              <RadioGroup value={channel} onValueChange={setChannel} className="mt-2 grid grid-cols-3 gap-2">
                <label
                  className={`border rounded-md p-2 text-xs cursor-pointer ${channel === "email" ? "border-[#FF5A00] bg-orange-50" : "border-zinc-200"} ${!customerHasEmail ? "opacity-50" : ""}`}
                  data-testid="pulse-reschedule-channel-email">
                  <RadioGroupItem value="email" className="sr-only" disabled={!customerHasEmail} />
                  Email only
                </label>
                <label
                  className={`border rounded-md p-2 text-xs cursor-pointer ${channel === "in_app" ? "border-[#FF5A00] bg-orange-50" : "border-zinc-200"}`}
                  data-testid="pulse-reschedule-channel-inapp">
                  <RadioGroupItem value="in_app" className="sr-only" />
                  In-app only
                </label>
                <label
                  className={`border rounded-md p-2 text-xs cursor-pointer ${channel === "both" ? "border-[#FF5A00] bg-orange-50" : "border-zinc-200"} ${!customerHasEmail ? "opacity-50" : ""}`}
                  data-testid="pulse-reschedule-channel-both">
                  <RadioGroupItem value="both" className="sr-only" disabled={!customerHasEmail} />
                  Email + In-app
                </label>
              </RadioGroup>
              {!customerHasEmail && (
                <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1">
                  <MailWarning className="w-3 h-3" /> Customer has no email on file — only in-app is available.
                </p>
              )}
            </div>

            <div className="mt-3">
              <Label className="text-[10px] uppercase font-mono-accent text-zinc-500">
                Customer message (editable)
              </Label>
              <Textarea
                rows={8}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="pulse-reschedule-message"
                className="mt-1 font-mono text-xs"
              />
            </div>

            {error && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 mt-2"
                   data-testid="pulse-reschedule-error">
                {error}
              </div>
            )}

            <DialogFooter className="gap-2 mt-2">
              <Button variant="ghost" onClick={closeAndReset} disabled={submitting}
                      data-testid="pulse-reschedule-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={doConfirm}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="pulse-reschedule-confirm-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Rescheduling…</>
                ) : (
                  <>Confirm reschedule</>
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm"
                 data-testid="pulse-reschedule-success">
              <div className="flex items-center gap-2 font-medium text-emerald-800">
                <CheckCircle2 className="w-4 h-4" /> Job rescheduled to {fmtDt(newSlot)}
              </div>
              <div className="text-xs text-emerald-700 mt-1">
                {customerName ? customerName : "The customer"}'s appointment for {jobTitle || "the job"} was moved.
              </div>
            </div>

            {result.notification && (
              <div className="mt-3 rounded-lg border border-zinc-200 p-3 text-sm">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent mb-1">
                  Notification delivery
                </div>
                {Object.keys(notifChannels).length === 0 && (
                  <div className="text-xs text-zinc-500" data-testid="pulse-reschedule-notif-none">
                    No channel selected — nothing was sent.
                  </div>
                )}
                {Object.entries(notifChannels).map(([chan, info]) => (
                  <div key={chan} className="flex items-center gap-2 text-xs mt-0.5"
                       data-testid={`pulse-reschedule-notif-${chan}`}>
                    {info.ok ? (
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    ) : (
                      <XCircle className="w-3 h-3 text-rose-600" />
                    )}
                    <span className="uppercase font-mono-accent">{chan}</span>
                    {info.ok ? (
                      <span className="text-emerald-700">delivered{info.to ? ` → ${info.to}` : ""}</span>
                    ) : (
                      <span className="text-rose-700" data-testid={`pulse-reschedule-notif-error-${chan}`}>
                        failed — {info.error || "unknown error"}
                      </span>
                    )}
                  </div>
                ))}

                {!notification.ok && (
                  <Button
                    onClick={doRetryNotification}
                    disabled={retrying}
                    variant="outline"
                    className="mt-2 h-8"
                    data-testid="pulse-reschedule-retry-notification-btn"
                  >
                    {retrying ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Retrying…</>
                    ) : (
                      <><RotateCcw className="w-3 h-3 mr-1" /> Retry notification</>
                    )}
                  </Button>
                )}
              </div>
            )}

            <DialogFooter className="mt-2">
              <Button onClick={closeAndReset} className="bg-zinc-900 text-white hover:bg-zinc-800"
                      data-testid="pulse-reschedule-close-btn">
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
